import { useMemo } from "react";
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation } from "react-router-dom";
import { Activity, MessageSquare, BarChart3, LogOut } from "lucide-react";
import { useWebSocket } from "./hooks/useWebSocket";
import { ProgressBar } from "./components/ProgressBar";
import { AccuracyTrend } from "./components/AccuracyTrend";
import { ScoreDistribution } from "./components/ScoreDistribution";
import { SuccessFailurePie } from "./components/SuccessFailurePie";
import { TaskList } from "./components/TaskList";
import { TraceViewer } from "./components/TraceViewer";
import { RunHistory } from "./components/RunHistory";
import AgentChat from "./components/AgentChat";
import ConfigurationPanel from "./components/ConfigurationPanel";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Login from "./components/Login";

function AppContent() {
  const state = useWebSocket();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, signOut } = useAuth();

  const isActive = useMemo(() => {
    return Boolean(state.run_id);
  }, [state.run_id]);

  // Show loading spinner while checking auth
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-amber-950/20">
        <div className="flex flex-col items-center gap-4">
          <div className="h-12 w-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!user) {
    return <Login />;
  }

  const handleSelectRun = async (runId: string) => {
    console.log("📂 Selecting run:", runId);

    // Check if this is the live run
    if (runId === state.liveRunId) {
      console.log("🔴 Switching to live run");
      await state.loadLiveRun();
      navigate('/dashboard');
    } else {
      // Load historical run
      console.log("📜 Loading historical run:", runId);
      await state.loadHistoricalRun(runId);
      navigate('/dashboard');
    }
  };

  const handleBackToLive = async () => {
    console.log("🔴 Back to live run");
    await state.loadLiveRun();
    navigate('/dashboard');
  };

  // Don't auto-redirect - let user navigate manually

  return (
    <div className="flex h-screen flex-col bg-gradient-to-br from-background via-background to-amber-950/20 overflow-hidden">
      {/* Header - Logo Left, Title Center, Menu Right */}
      <header className="border-b border-primary/30 glass-header flex-shrink-0 relative z-[200]">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between max-w-full">
            {/* Logo - Snug glass capsule - Clickable to home */}
            <div className="flex items-center gap-4 flex-shrink-0">
              <Link to="/">
                <div className="relative px-4 py-2 rounded-full backdrop-blur-2xl bg-gradient-to-br from-primary/40 via-orange-500/30 to-amber-500/40 border-2 border-primary/60 shadow-lg hover:shadow-amber-500/50 hover:border-primary/80 hover:scale-105 transition-all duration-300 cursor-pointer">
                  {/* Strong glow effect */}
                  <div className="absolute inset-0 rounded-full bg-gradient-to-br from-primary/50 to-amber-500/50 blur-lg opacity-60 animate-pulse" />
                  {/* Logo - Natural aspect ratio */}
                  <div className="relative">
                    <img
                      src="/assets/logo.png"
                      alt="Simon Loos Logo"
                      className="h-12 w-auto object-contain relative z-10 drop-shadow-lg"
                      onError={(e) => {
                        // Fallback to icon if logo doesn't exist
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                    <Activity className="h-12 w-12 text-primary animate-pulse hidden relative z-10" />
                  </div>
                </div>
              </Link>
            </div>

            {/* Title - Centered */}
            <div className="flex-1 flex justify-center">
              <div className="text-center">
                <h1 className="text-2xl font-bold bg-gradient-to-r from-primary via-orange-400 to-amber-300 bg-clip-text text-transparent">
                  Simon Loos BI Agent
                </h1>
              </div>
            </div>

            {/* User Info and Logout */}
            <div className="flex items-center gap-4 flex-shrink-0">
              {/* Navigation Links */}
              <nav className="hidden md:flex items-center gap-1">
                <Link
                  to="/dashboard"
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                    location.pathname === '/dashboard'
                      ? 'bg-primary/20 text-primary'
                      : 'text-muted-foreground hover:text-foreground hover:bg-primary/10'
                  }`}
                >
                  <BarChart3 className="h-4 w-4" />
                  Dashboard
                </Link>
                <Link
                  to="/chat"
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                    location.pathname === '/chat'
                      ? 'bg-primary/20 text-primary'
                      : 'text-muted-foreground hover:text-foreground hover:bg-primary/10'
                  }`}
                >
                  <MessageSquare className="h-4 w-4" />
                  Chat
                </Link>
              </nav>

              {/* Divider */}
              <div className="h-8 w-px bg-primary/20 hidden md:block" />

              {/* User Avatar with Dropdown */}
              <div className="relative group">
                <button
                  className="flex items-center justify-center h-10 w-10 rounded-full bg-primary/20 border-2 border-primary/40 hover:border-primary/80 hover:bg-primary/30 transition-all duration-200 cursor-pointer"
                  title={user.displayName || user.email || 'User'}
                >
                  {user.photoURL ? (
                    <img
                      src={user.photoURL}
                      alt={user.displayName || user.email || 'User'}
                      className="h-full w-full rounded-full object-cover"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <span className="text-sm font-semibold text-primary">
                      {(() => {
                        const name = user.displayName || user.email || '';
                        const parts = name.split(/[@\s]+/);
                        if (parts.length >= 2 && parts[0] && parts[1]) {
                          return (parts[0][0] + parts[1][0]).toUpperCase();
                        }
                        return name.slice(0, 2).toUpperCase();
                      })()}
                    </span>
                  )}
                </button>
                {/* Dropdown */}
                <div className="absolute right-0 top-full mt-2 w-48 py-2 rounded-lg bg-card border border-primary/30 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <div className="px-3 py-2 border-b border-primary/20">
                    <p className="text-sm font-medium text-foreground truncate">{user.displayName || 'User'}</p>
                    <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                  </div>
                  <button
                    onClick={() => signOut()}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                  >
                    <LogOut className="h-4 w-4" />
                    Sign out
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content with Sidebar */}
      <main className="flex-1 overflow-hidden flex">
        <Routes>
          {/* Home Route */}
          <Route path="/" element={
            <div className="flex-1 overflow-y-auto bg-gradient-to-br from-background via-background to-primary/5 flex items-center justify-center p-8">
              <div className="w-full max-w-5xl">
                <div className="text-center mb-12">
                  <p className="text-xl text-muted-foreground">
                    Ask questions about your data in natural language
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Chat with Agent */}
                  <Link
                    to="/chat"
                    className="group relative p-8 rounded-2xl backdrop-blur-2xl bg-gradient-to-br from-blue-500/20 via-indigo-500/10 to-purple-500/20 border-2 border-blue-500/40 hover:border-blue-500/80 hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-blue-500/50"
                  >
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-500/30 to-purple-500/30 blur-xl opacity-0 group-hover:opacity-60 transition-opacity" />
                    <div className="relative">
                      <div className="flex justify-center mb-4">
                        <div className="p-4 rounded-full bg-blue-500/30 group-hover:bg-blue-500/50 transition-colors">
                          <MessageSquare className="h-12 w-12 text-blue-400" />
                        </div>
                      </div>
                      <h2 className="text-2xl font-bold text-foreground mb-2">Chat</h2>
                      <p className="text-muted-foreground">
                        Ask questions and get answers from your data warehouse
                      </p>
                    </div>
                  </Link>

                  {/* Start Evaluation */}
                  <Link
                    to="/configuration"
                    className="group relative p-8 rounded-2xl backdrop-blur-2xl bg-gradient-to-br from-success/20 via-green-500/10 to-emerald-500/20 border-2 border-success/40 hover:border-success/80 hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-success/50"
                  >
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-success/30 to-emerald-500/30 blur-xl opacity-0 group-hover:opacity-60 transition-opacity" />
                    <div className="relative">
                      <div className="flex justify-center mb-4">
                        <div className="p-4 rounded-full bg-success/30 group-hover:bg-success/50 transition-colors">
                          <Activity className="h-12 w-12 text-success" />
                        </div>
                      </div>
                      <h2 className="text-2xl font-bold text-foreground mb-2">Start Evaluation</h2>
                      <p className="text-muted-foreground">
                        Run the agent against test datasets
                      </p>
                    </div>
                  </Link>

                  {/* Evaluation Results */}
                  <Link
                    to="/dashboard"
                    className="group relative p-8 rounded-2xl backdrop-blur-2xl bg-gradient-to-br from-primary/20 via-amber-500/10 to-orange-500/20 border-2 border-primary/40 hover:border-primary/80 hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-amber-500/50"
                  >
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-primary/30 to-orange-500/30 blur-xl opacity-0 group-hover:opacity-60 transition-opacity" />
                    <div className="relative">
                      <div className="flex justify-center mb-4">
                        <div className="p-4 rounded-full bg-primary/30 group-hover:bg-primary/50 transition-colors">
                          <BarChart3 className="h-12 w-12 text-primary" />
                        </div>
                      </div>
                      <h2 className="text-2xl font-bold text-foreground mb-2">Evaluation Results</h2>
                      <p className="text-muted-foreground">
                        View results and analyze performance
                      </p>
                    </div>
                  </Link>
                </div>
              </div>
            </div>
          } />

          {/* Configuration Route */}
          <Route path="/configuration" element={
            <div className="flex-1 overflow-y-auto bg-background">
              <ConfigurationPanel onNavigateToDashboard={() => navigate('/dashboard')} />
            </div>
          } />

          {/* Chat Route - Full page agent chat */}
          <Route path="/chat" element={
            <div className="flex-1 overflow-hidden">
              <AgentChat />
            </div>
          } />


          {/* Dashboard Route */}
          <Route path="/dashboard" element={
          <>
            {/* Left: Run History Sidebar */}
            <aside className="w-80 border-r border-primary/30 glass-header overflow-hidden flex-shrink-0">
              <div className="h-full p-6 overflow-y-auto">
                <RunHistory
                  onSelectRun={handleSelectRun}
                  liveRunId={state.liveRunId}
                  liveRunInfo={state.viewMode === 'live' ? {
                    run_id: state.run_id || '',
                    agent_path: state.agent_path,
                    model: state.model,
                    dataset_version: state.dataset_version,
                    total_questions: state.total_questions,
                    mean_accuracy: state.metrics?.mean_accuracy
                  } : undefined}
                  currentRunId={state.run_id}
                  viewMode={state.viewMode}
                  onBackToLive={handleBackToLive}
                />
              </div>
            </aside>

            {/* Right: Main Dashboard - Centered with max-width */}
            <div className="flex-1 overflow-y-auto flex justify-center">
              <div className="w-full max-w-7xl px-6 py-6">
        {!isActive ? (
          <div className="flex h-full flex-col items-center justify-center">
            <p className="text-muted-foreground">No active evaluation</p>
          </div>
        ) : (
          <div className="flex flex-col gap-6 pb-6">
            {/* Configuration Info */}
            <div className="flex-shrink-0 rounded-xl border border-primary/20 bg-card/50 p-4 backdrop-blur-sm">
              <div className="grid grid-cols-5 gap-4 text-sm">
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Agent</div>
                  <div className="font-mono text-primary truncate" title={state.agent_path}>
                    {state.agent_path ? (() => {
                      const parts = state.agent_path.split('/');
                      return parts.length >= 2 ? parts[parts.length - 2] : parts[0] || 'N/A';
                    })() : 'N/A'}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Model</div>
                  <div className="font-mono text-primary truncate" title={state.model}>
                    {state.model || 'gemini-2.5-flash'}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Dataset</div>
                  <div className="font-mono text-primary">
                    {state.dataset_version || 'N/A'}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Workers</div>
                  <div className="font-mono text-primary">
                    {state.parallel_workers || 'N/A'}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Run ID</div>
                  <div className="font-mono text-primary truncate" title={state.run_id}>
                    {state.run_id || 'N/A'}
                  </div>
                </div>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="flex-shrink-0 rounded-xl border border-primary/20 bg-card/50 p-6 backdrop-blur-sm glow">
              <ProgressBar state={state} />
            </div>

            {/* Charts Section: 2x2 Grid with Fixed Heights */}
            <div className="grid grid-cols-2 gap-6 flex-shrink-0">
              <div className="rounded-xl border border-primary/20 bg-card/50 p-6 backdrop-blur-sm overflow-hidden h-96">
                <AccuracyTrend metrics={state.metrics} tasks={state.tasks} totalQuestions={state.total_questions} />
              </div>

              <div className="rounded-xl border border-primary/20 bg-card/50 p-6 backdrop-blur-sm overflow-hidden h-96">
                <ScoreDistribution metrics={state.metrics} tasks={state.tasks} />
              </div>

              <div className="rounded-xl border border-primary/20 bg-card/50 p-6 backdrop-blur-sm overflow-hidden h-96">
                <SuccessFailurePie metrics={state.metrics} tasks={state.tasks} totalQuestions={state.total_questions} />
              </div>

              <div className="rounded-xl border border-primary/20 bg-card/50 p-6 backdrop-blur-sm overflow-hidden h-96">
                <SuccessFailurePie metrics={state.metrics} tasks={state.tasks} totalQuestions={state.total_questions} type="sql" />
              </div>
            </div>

            {/* Bottom Section: Task List */}
            <div className="h-[800px] rounded-xl border border-primary/20 bg-card/50 p-6 backdrop-blur-sm">
              <div className="mb-4 flex items-center justify-between flex-shrink-0">
                <h3 className="text-lg font-bold bg-gradient-to-r from-primary to-orange-400 bg-clip-text text-transparent">
                  Task Queue
                </h3>
                <div className="flex items-center gap-3 text-xs">
                  <div className="flex items-center gap-1.5">
                    <div className="h-2 w-2 rounded-full bg-blue-500" />
                    <span className="text-muted-foreground">Queued</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="h-2 w-2 rounded-full bg-warning" />
                    <span className="text-muted-foreground">Running</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="h-2 w-2 rounded-full bg-success" />
                    <span className="text-muted-foreground">Completed</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="h-2 w-2 rounded-full bg-destructive" />
                    <span className="text-muted-foreground">Failed</span>
                  </div>
                </div>
              </div>
              <div className="h-[calc(100%-3.5rem)] overflow-hidden">
                <TaskList tasks={state.tasks} onTaskClick={state.openTraceViewer} totalQuestions={state.total_questions} />
              </div>
            </div>
          </div>
        )}
              </div>
            </div>
          </>
          } />
        </Routes>
      </main>

      {/* Trace Viewer Modal */}
      {state.activeTraceView && state.tasks[state.activeTraceView] && (
        <TraceViewer
          questionId={state.activeTraceView}
          question={state.tasks[state.activeTraceView].question}
          traces={state.traces[state.activeTraceView] || []}
          sql={state.tasks[state.activeTraceView].sql}
          answer={state.tasks[state.activeTraceView].answer}
          evaluation={state.tasks[state.activeTraceView].evaluation}
          onClose={state.closeTraceViewer}
        />
      )}

    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
